import "./Modal.scss";

import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FCCC } from "types";

import { Close } from "@/assets/icons/icons";

interface Props {
  title: ReactNode;
  onClose: () => void;
  classNameDescription?: string;
  showCloseButton?: boolean;
  hasScroll?: boolean;
}

export const Modal: FCCC<Props> = ({
  children,
  className,
  classNameDescription,
  title,
  onClose,
  showCloseButton = true,
  hasScroll = true,
}) => {
  const { t } = useTranslation();

  const titleType = typeof title === "string" ? "h2" : "div";

  return (
    <Transition.Root as={Fragment} appear show>
      <Dialog
        className="relative z-30"
        as="div"
        onClose={onClose}
        data-testid="modal"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-10 bg-primary-100 opacity-25 transition-opacity" />
        </Transition.Child>
        <div id="modal" className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  "mx-6 my-auto max-w-[520px] overflow-hidden bg-white p-6 pr-0 pt-4 md:p-8 md:pr-0",
                  className,
                )}
              >
                {/*The padding right is set on the lower containers so that the scrollbar is on the very right*/}
                <div className="flex flex-row justify-between gap-2.5 pb-6 pr-6 md:pr-8">
                  <Dialog.Title className="text-primary-100" as={titleType}>
                    {title}
                  </Dialog.Title>
                  {showCloseButton && (
                    <button
                      aria-label={t("common.buttons.abort")}
                      className="modal-close-button flex"
                      onClick={onClose}
                    >
                      <Close className="mt-1 w-6 cursor-auto text-black" />
                    </button>
                  )}
                </div>
                <Dialog.Description
                  className={clsx(
                    "pr-6 md:pr-8",
                    hasScroll &&
                      "max-h-[336px] overflow-y-auto sm:max-h-[372px]",
                    classNameDescription,
                  )}
                  as="div"
                >
                  {children}
                </Dialog.Description>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
